import { useSession } from "@/services/auth0/session";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

const API_URL = import.meta.env.VITE_API_URL;

export const providerKindSchema = z.enum(["AGGREGATOR", "DIRECT"], {
  required_error: "Choose a provider classification",
});

// Schema for API Provider
export const apiProviderSchema = z.object({
  id: z.string(),
  code: z.string(),
  kind: providerKindSchema,
  name: z.string(),
  baseUrl: z.string().url(),
  supportedCarrierCodes: z.array(z.string()),
  active: z.boolean(),
  credentials: z.record(z.string()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ApiProvider = z.infer<typeof apiProviderSchema>;

// Create API Provider schema
export const createApiProviderSchema = z.object({
  code: z.string().min(1, "Code is required"),
  kind: providerKindSchema,
  credentials: z.record(z.string()).optional(),
  name: z.string().min(1, "Name is required"),
  baseUrl: z.string().url("Must be a valid URL"),
  supportedCarrierCodes: z.array(z.string()).default([]),
  active: z.boolean().default(true),
});

export type CreateApiProvider = z.infer<typeof createApiProviderSchema>;

// Update API Provider schema
export type UpdateApiProvider = Partial<CreateApiProvider>;

const runtimeRefreshFailureSchema = z.object({
  code: z.literal("PROVIDER_RUNTIME_REFRESH_FAILED"),
  providerId: z.string().min(1),
});

export function getSavedProviderId(error: unknown): string | undefined {
  if (!axios.isAxiosError(error) || error.response?.status !== 503) return;
  const result = runtimeRefreshFailureSchema.safeParse(error.response.data);
  return result.success ? result.data.providerId : undefined;
}

// ========================= API Hooks =========================

// Get all API providers
export function useApiProviders() {
  const { token } = useSession();

  return useQuery({
    queryKey: ["api-providers"],
    queryFn: async () => {
      const { data } = await axios.get<ApiProvider[]>(
        `${API_URL}/api/v2/api-providers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get API provider by ID
export function useApiProvider(id: string, enabled = true) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["api-providers", id],
    queryFn: async () => {
      const { data } = await axios.get<ApiProvider>(
        `${API_URL}/api/v2/api-providers/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!token && !!id && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Create API provider
export function useCreateApiProvider() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateApiProvider) => {
      const response = await axios.post<ApiProvider>(
        `${API_URL}/api/v2/api-providers`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onError: (error) => {
      const savedId = getSavedProviderId(error);
      if (savedId) {
        void queryClient.invalidateQueries({
          queryKey: ["api-providers"],
          exact: true,
        });
        void queryClient.invalidateQueries({
          queryKey: ["api-providers", savedId],
          refetchType: "none",
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["api-providers"] });
    },
  });
}

// Update API provider
export function useUpdateApiProvider() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateApiProvider;
    }) => {
      const response = await axios.patch<ApiProvider>(
        `${API_URL}/api/v2/api-providers/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onError: (error) => {
      const savedId = getSavedProviderId(error);
      if (savedId) {
        void queryClient.invalidateQueries({
          queryKey: ["api-providers"],
          exact: true,
        });
        void queryClient.invalidateQueries({
          queryKey: ["api-providers", savedId],
          refetchType: "none",
        });
      }
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["api-providers"] });
      void queryClient.invalidateQueries({
        queryKey: ["api-providers", variables.id],
      });
    },
  });
}

// Delete API provider
export function useDeleteApiProvider() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/api-providers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["api-providers"] });
    },
  });
}

// Schema for carrier from API provider
export const providerCarrierSchema = z.object({
  carrierId: z.string(),
  carrierCode: z.string(),
  name: z.string(),
  nickname: z.string().optional(),
  accountNumber: z.string().optional(),
  primary: z.boolean(),
  services: z.array(
    z.object({
      service_code: z.string(),
      name: z.string(),
      domestic: z.boolean(),
      international: z.boolean(),
      is_multi_package_supported: z.boolean(),
    })
  ),
  packages: z.array(
    z.object({
      package_code: z.string(),
      name: z.string(),
      domestic: z.boolean(),
      international: z.boolean(),
    })
  ),
  options: z.array(
    z.object({
      name: z.string(),
      default_value: z.string(),
      description: z.string(),
    })
  ),
});

export type ProviderCarrier = z.infer<typeof providerCarrierSchema>;

// Get carriers from API provider
export function useProviderCarriers(providerId: string, enabled = true) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["provider-carriers", providerId],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: ProviderCarrier[];
        metadata: Record<string, unknown>;
      }>(
        `${API_URL}/api/v2/carriers/admin/api-providers/${providerId}/carriers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    enabled: !!token && !!providerId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
